// frontend/src/components/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  TrendingUp,
  DollarSign,
  Search,
  BarChart3,
  Settings,
  Shield,
  UserCheck,
  UserX,
  Crown,
  AlertTriangle,
  CheckCircle,
  Edit,
  Trash2,
  Plus,
  MousePointer,
  Eye,
  Target,
  Globe,
  Smartphone,
  Monitor,
  Tag,
  Calendar,
  Percent,
  Car,
  Wrench,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  MailX,
  Activity,
  Inbox,
  Megaphone,
  Receipt,
  FileText,
  Boxes,
  Landmark
} from 'lucide-react';
import { useAuth } from '../auth';
import { PageHeader } from '../common';
import api from '../../services/api';
import OpsAds from './ops/OpsAds';
import OpsExpenses from './ops/OpsExpenses';
import OpsOrdering from './ops/OpsOrdering';
import OpsInventory from './ops/OpsInventory';
import OpsFinances from './ops/OpsFinances';

export const AdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marketingData, setMarketingData] = useState(null);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const [promoStats, setPromoStats] = useState(null);
  const [recentSignups, setRecentSignups] = useState([]);
  const [userBreakdown, setUserBreakdown] = useState(null);

  useEffect(() => {
    loadAnalytics();
    loadRecentSignups();
    loadUserBreakdown();
    loadPromoStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    }
    if (activeTab === 'marketing') {
      loadMarketingData();
    }
  }, [activeTab]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await api.getAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      // Set default values if API fails
      setAnalytics({
        total_users: 0,
        active_subscriptions: 0,
        monthly_revenue: 0,
        total_searches: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await api.getAllUsers();
      setUsers(data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMarketingData = async () => {
    try {
      setMarketingLoading(true);
      const response = await api.get('/marketing-analytics/dashboard?days=30');
      setMarketingData(response.data);
    } catch (error) {
      console.error('Failed to load marketing data:', error);
      setMarketingData(null);
    } finally {
      setMarketingLoading(false);
    }
  };

  const loadRecentSignups = async () => {
    try {
      const response = await api.get('/admin/recent-signups?limit=10');
      setRecentSignups(response.data?.signups || []);
    } catch (error) {
      console.error('Failed to load recent signups:', error);
      setRecentSignups([]);
    }
  };

  const loadUserBreakdown = async () => {
    try {
      const response = await api.get('/admin/user-breakdown');
      setUserBreakdown(response.data);
    } catch (error) {
      console.error('Failed to load user breakdown:', error);
      setUserBreakdown(null);
    }
  };

  const loadPromoStats = async () => {
    try {
      const response = await api.get('/admin/promo-stats');
      setPromoStats(response.data);
    } catch (error) {
      console.error('Failed to load promo stats:', error);
      setPromoStats(null);
    }
  };

  const handleUserAction = async (userId, action) => {
    try {
      if (action === 'delete') {
        if (window.confirm('Are you sure you want to delete this user?')) {
          await api.deleteUser(userId);
          await loadUsers();
        }
      } else if (action === 'toggle_status') {
        const userData = users.find(u => u.id === userId);
        await api.updateUser(userId, { is_active: !userData.is_active });
        await loadUsers();
      }
    } catch (error) {
      console.error('User action failed:', error);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    await Promise.all([
      loadAnalytics(),
      loadRecentSignups(),
      loadUserBreakdown(),
      loadPromoStats()
    ]);
    setLoading(false);
  };

  const StatCard = ({ title, value, icon: Icon, color, trend, subtitle }) => (
    <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-ink-muted">{title}</p>
          <p className="text-2xl font-bold text-ink-soft">{value}</p>
          {subtitle && (
            <p className="text-small text-ink-muted mt-1">{subtitle}</p>
          )}
          {trend !== undefined && trend !== null && (
            <div className={`flex items-center mt-2 text-sm ${
              trend >= 0 ? 'text-success' : 'text-danger'
            }`}>
              {trend >= 0 ? (
                <ArrowUpRight className="w-4 h-4 mr-1" />
              ) : (
                <ArrowDownRight className="w-4 h-4 mr-1" />
              )}
              <span>{Math.abs(trend)}% from last month</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const UserCard = ({ user: userData }) => (
    <div className="bg-surface-card rounded-card shadow-elevation-1 border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            userData.is_active ? 'bg-success-tint' : 'bg-surface-muted'
          }`}>
            {userData.is_active ? (
              <UserCheck className="w-5 h-5 text-success" />
            ) : (
              <UserX className="w-5 h-5 text-ink-muted" />
            )}
          </div>
          <div>
            <h3 className="font-medium text-ink-soft">{userData.full_name}</h3>
            <p className="text-sm text-ink-muted">{userData.email}</p>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`px-2 py-1 rounded-full text-small font-medium ${
                userData.user_type === 'admin' ? 'bg-danger-tint text-danger' :
                userData.user_type === 'dealer' ? 'bg-info-tint text-info' :
                userData.user_type === 'mechanic' ? 'bg-info-tint text-info' :
                'bg-success-tint text-success'
              }`}>
                {userData.user_type}
              </span>
              <span className={`px-2 py-1 rounded-full text-small font-medium ${
                userData.subscription_tier === 'enterprise' ? 'bg-warning-tint text-warning' :
                userData.subscription_tier === 'shop_pro' ? 'bg-info-tint text-info' :
                userData.subscription_tier === 'professional' ? 'bg-success-tint text-success' :
                'bg-surface-muted text-ink-soft'
              }`}>
                {userData.subscription_tier || 'free'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleUserAction(userData.id, 'toggle_status')}
            className={`p-2 rounded-lg text-sm font-medium ${
              userData.is_active
                ? 'bg-danger-tint text-danger hover:bg-danger-tint/80'
                : 'bg-success-tint text-success hover:bg-success-tint/80'
            }`}
          >
            {userData.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => handleUserAction(userData.id, 'delete')}
            className="p-2 rounded-lg bg-danger-tint text-danger hover:bg-danger-tint/80"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const TabButton = ({ id, label, icon: Icon, isActive, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
        isActive
          ? 'bg-accent-tint text-accent'
          : 'text-ink-muted hover:text-ink-soft hover:bg-surface-muted'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );

  // Check if user is admin
  const isAdmin = user?.is_admin || user?.user_type === 'admin';
  
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-surface-page flex items-center justify-center">
        <div className="bg-surface-card rounded-card shadow-elevation-1 border p-8 text-center">
          <Shield className="w-12 h-12 text-danger mx-auto mb-4" />
          <h2 className="text-xl font-bold text-ink-soft mb-2">Access Denied</h2>
          <p className="text-ink-muted">You don't have permission to access the admin dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <PageHeader title="Admin Dashboard" />
            <p className="text-ink-muted">Manage users, view analytics, and monitor system performance</p>
          </div>
          <button 
            onClick={refreshData}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-4 mb-8 flex-wrap gap-2">
          <TabButton
            id="overview"
            label="Overview"
            icon={BarChart3}
            isActive={activeTab === 'overview'}
            onClick={setActiveTab}
          />
          <TabButton
            id="marketing"
            label="Marketing"
            icon={Target}
            isActive={activeTab === 'marketing'}
            onClick={setActiveTab}
          />
          <TabButton
            id="users"
            label="Users"
            icon={Users}
            isActive={activeTab === 'users'}
            onClick={setActiveTab}
          />
          <TabButton
            id="settings"
            label="Settings"
            icon={Settings}
            isActive={activeTab === 'settings'}
            onClick={setActiveTab}
          />
          <span className="self-center px-2 text-ink-muted/50">|</span>
          <TabButton id="ads" label="Ads" icon={Megaphone} isActive={activeTab === 'ads'} onClick={setActiveTab} />
          <TabButton id="expenses" label="Expenses" icon={Receipt} isActive={activeTab === 'expenses'} onClick={setActiveTab} />
          <TabButton id="ordering" label="Ordering & Invoices" icon={FileText} isActive={activeTab === 'ordering'} onClick={setActiveTab} />
          <TabButton id="inventory" label="Inventory" icon={Boxes} isActive={activeTab === 'inventory'} onClick={setActiveTab} />
          <TabButton id="finances" label="Finances" icon={Landmark} isActive={activeTab === 'finances'} onClick={setActiveTab} />
          <Link
            to="/admin/outreach/queue"
            className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors text-ink-muted hover:text-ink-soft hover:bg-surface-muted"
          >
            <Inbox className="w-4 h-4" />
            <span>Outreach Queue</span>
          </Link>
          <Link
            to="/admin/outreach/suppression"
            className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors text-ink-muted hover:text-ink-soft hover:bg-surface-muted"
          >
            <MailX className="w-4 h-4" />
            <span>Outreach Suppression</span>
          </Link>
          <Link
            to="/admin/outreach/runs"
            className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors text-ink-muted hover:text-ink-soft hover:bg-surface-muted"
          >
            <Activity className="w-4 h-4" />
            <span>Outreach Runs</span>
          </Link>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Main Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Users"
                value={analytics?.total_users || 0}
                icon={Users}
                color="bg-accent"
                trend={analytics?.user_growth}
              />
              <StatCard
                title="Active Subscriptions"
                value={analytics?.active_subscriptions || 0}
                icon={Crown}
                color="bg-info"
                trend={analytics?.subscription_growth}
              />
              <StatCard
                title="Monthly Revenue"
                value={`$${analytics?.monthly_revenue || 0}`}
                icon={DollarSign}
                color="bg-success"
                trend={analytics?.revenue_growth}
              />
              <StatCard
                title="Parts Searches"
                value={analytics?.total_searches || 0}
                icon={Search}
                color="bg-warning"
                trend={analytics?.search_growth}
              />
            </div>

            {/* User Breakdown & Subscription Tiers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* User Type Breakdown */}
              <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
                <h3 className="text-lg font-semibold text-ink-soft mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-info" />
                  User Breakdown
                </h3>
                {userBreakdown ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-info-tint rounded-lg">
                      <div className="flex items-center">
                        <Wrench className="w-5 h-5 text-info mr-3" />
                        <span className="font-medium text-ink-soft">Mechanics</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xl font-bold text-info">{userBreakdown.mechanics || 0}</span>
                        <span className="text-ink-muted text-sm ml-1">
                          ({userBreakdown.total > 0 ? Math.round((userBreakdown.mechanics / userBreakdown.total) * 100) : 0}%)
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-success-tint rounded-lg">
                      <div className="flex items-center">
                        <Car className="w-5 h-5 text-success mr-3" />
                        <span className="font-medium text-ink-soft">Car Owners</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xl font-bold text-success">{userBreakdown.car_owners || 0}</span>
                        <span className="text-ink-muted text-sm ml-1">
                          ({userBreakdown.total > 0 ? Math.round((userBreakdown.car_owners / userBreakdown.total) * 100) : 0}%)
                        </span>
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="flex items-center justify-between text-ink-muted">
                        <span>Total Users</span>
                        <span className="font-bold text-ink-soft">{userBreakdown.total || 0}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-info-tint rounded-lg">
                      <div className="flex items-center">
                        <Wrench className="w-5 h-5 text-info mr-3" />
                        <span className="font-medium text-ink-soft">Mechanics</span>
                      </div>
                      <span className="text-xl font-bold text-info">-</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-success-tint rounded-lg">
                      <div className="flex items-center">
                        <Car className="w-5 h-5 text-success mr-3" />
                        <span className="font-medium text-ink-soft">Car Owners</span>
                      </div>
                      <span className="text-xl font-bold text-success">-</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Subscription Tiers */}
              <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
                <h3 className="text-lg font-semibold text-ink-soft mb-4 flex items-center">
                  <Crown className="w-5 h-5 mr-2 text-info" />
                  Subscription Tiers
                </h3>
                {userBreakdown?.tiers ? (
                  <div className="space-y-3">
                    {[
                      { name: 'Free', count: userBreakdown.tiers.free || 0, color: 'bg-surface-muted text-ink-soft' },
                      { name: 'Basic', count: userBreakdown.tiers.basic || 0, color: 'bg-info-tint text-info' },
                      { name: 'Professional', count: userBreakdown.tiers.professional || 0, color: 'bg-success-tint text-success' },
                      { name: 'Enterprise', count: userBreakdown.tiers.enterprise || 0, color: 'bg-info-tint text-info' },
                    ].map((tier) => (
                      <div key={tier.name} className="flex items-center justify-between p-3 bg-surface-page rounded-lg">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${tier.color}`}>
                          {tier.name}
                        </span>
                        <span className="font-bold text-ink-soft">{tier.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {['Free', 'Basic', 'Professional', 'Enterprise'].map((tier) => (
                      <div key={tier} className="flex items-center justify-between p-3 bg-surface-page rounded-lg">
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-surface-muted text-ink-soft">
                          {tier}
                        </span>
                        <span className="font-bold text-ink-muted">-</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Promo Code Stats */}
            <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
              <h3 className="text-lg font-semibold text-ink-soft mb-4 flex items-center">
                <Tag className="w-5 h-5 mr-2 text-warning" />
                Promo Code Usage
              </h3>
              {promoStats ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-warning-tint rounded-lg text-center">
                    <p className="text-2xl font-bold text-warning">{promoStats.total_uses || 0}</p>
                    <p className="text-sm text-ink-muted">Total Uses</p>
                  </div>
                  <div className="p-4 bg-success-tint rounded-lg text-center">
                    <p className="text-2xl font-bold text-success">${promoStats.total_discount || 0}</p>
                    <p className="text-sm text-ink-muted">Total Discounts</p>
                  </div>
                  <div className="p-4 bg-info-tint rounded-lg text-center">
                    <p className="text-2xl font-bold text-info">{promoStats.active_codes || 0}</p>
                    <p className="text-sm text-ink-muted">Active Codes</p>
                  </div>
                  <div className="p-4 bg-info-tint rounded-lg text-center">
                    <p className="text-2xl font-bold text-info">{promoStats.top_code || '-'}</p>
                    <p className="text-sm text-ink-muted">Most Used</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-ink-muted">
                  <Tag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No promo code data available</p>
                </div>
              )}
            </div>

            {/* Recent Signups */}
            <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
              <h3 className="text-lg font-semibold text-ink-soft mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-success" />
                Recent Signups
              </h3>
              {recentSignups.length > 0 ? (
                <div className="space-y-3">
                  {recentSignups.slice(0, 5).map((signup, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-surface-page rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-success-tint rounded-full flex items-center justify-center">
                          <UserCheck className="w-4 h-4 text-success" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-ink-soft">{signup.full_name || signup.email}</p>
                          <p className="text-small text-ink-muted">{signup.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-small font-medium ${
                          signup.user_type === 'mechanic' ? 'bg-info-tint text-info' : 'bg-success-tint text-success'
                        }`}>
                          {signup.user_type}
                        </span>
                        <p className="text-small text-ink-muted mt-1">
                          {signup.created_at ? new Date(signup.created_at).toLocaleDateString() : 'Recently'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-ink-muted">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No recent signups to display</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'marketing' && (
          <div className="space-y-8">
            {/* Info Banner - show different message based on setup status */}
            {marketingData?.tables_exist === false && (
              <div className="bg-warning-tint border border-warning-border rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-warning mt-0.5 mr-3" />
                  <div>
                    <h4 className="font-medium text-warning">Database Setup Required</h4>
                    <p className="text-sm text-warning mt-1">
                      {marketingData.message || "Marketing analytics tables haven't been created yet."}
                    </p>
                    <p className="text-sm text-warning mt-2">
                      Run this command to set up the tables:
                    </p>
                    <code className="block mt-1 p-2 bg-warning-tint rounded text-small font-mono">
                      psql -d crow_automotive -f db/migrations/010_marketing_analytics.sql
                    </code>
                  </div>
                </div>
              </div>
            )}
            
            {marketingData?.tables_exist !== false && (
              <div className="bg-info-tint border border-info-border rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-info mt-0.5 mr-3" />
                  <div>
                    <h4 className="font-medium text-info">Marketing Analytics Active</h4>
                    <p className="text-sm text-info mt-1">
                      Tracking page views, CTA clicks, and conversions from your landing pages.
                      Make sure the tracking script is deployed to your sales website.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Marketing Stats Grid */}
            {marketingLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-surface-card rounded-card shadow-elevation-1 border p-6 animate-pulse">
                    <div className="h-4 bg-surface-muted rounded w-1/2 mb-2"></div>
                    <div className="h-8 bg-surface-muted rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : marketingData ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatCard
                    title="Page Views (30d)"
                    value={marketingData.overview?.total_page_views || 0}
                    icon={Eye}
                    color="bg-accent"
                  />
                  <StatCard
                    title="Unique Visitors"
                    value={marketingData.overview?.unique_visitors || 0}
                    icon={Users}
                    color="bg-info"
                  />
                  <StatCard
                    title="CTA Clicks"
                    value={marketingData.overview?.total_cta_clicks || 0}
                    icon={MousePointer}
                    color="bg-success"
                  />
                  <StatCard
                    title="Click Rate"
                    value={`${marketingData.overview?.cta_click_rate || 0}%`}
                    icon={Target}
                    color="bg-warning"
                  />
                </div>

                {/* Conversion Funnel */}
                <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
                  <h3 className="text-lg font-semibold text-ink-soft mb-4">Conversion Funnel (30 days)</h3>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    {[
                      { label: 'Visits', value: marketingData.funnel?.visits || 0, color: 'bg-info-tint text-info' },
                      { label: 'CTA Clicks', value: marketingData.funnel?.cta_clicks || 0, color: 'bg-info-tint text-info' },
                      { label: 'Signup Starts', value: marketingData.funnel?.signup_starts || 0, color: 'bg-warning-tint text-warning' },
                      { label: 'Signups', value: marketingData.funnel?.signups_completed || 0, color: 'bg-success-tint text-success' },
                      { label: 'Subscriptions', value: marketingData.funnel?.subscriptions || 0, color: 'bg-success-tint text-success' },
                    ].map((step, index, arr) => (
                      <div key={step.label} className="flex items-center">
                        <div className={`px-4 py-3 rounded-lg ${step.color}`}>
                          <p className="text-small font-medium opacity-75">{step.label}</p>
                          <p className="text-2xl font-bold">{step.value}</p>
                        </div>
                        {index < arr.length - 1 && (
                          <div className="mx-2 text-ink-muted hidden md:block">→</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Pages & Devices */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
                    <h3 className="text-lg font-semibold text-ink-soft mb-4 flex items-center">
                      <Globe className="w-5 h-5 mr-2 text-info" />
                      Top Pages
                    </h3>
                    <div className="space-y-3">
                      {marketingData.top_pages?.slice(0, 5).map((page, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-surface-page rounded-lg">
                          <span className="text-sm font-medium text-ink-soft truncate max-w-[200px]">
                            {page.page_path || '/'}
                          </span>
                          <div className="text-right">
                            <span className="text-sm font-bold text-ink-soft">{page.views}</span>
                            <span className="text-small text-ink-muted ml-1">views</span>
                          </div>
                        </div>
                      ))}
                      {(!marketingData.top_pages || marketingData.top_pages.length === 0) && (
                        <p className="text-ink-muted text-center py-4">No data yet</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
                    <h3 className="text-lg font-semibold text-ink-soft mb-4 flex items-center">
                      <Monitor className="w-5 h-5 mr-2 text-info" />
                      Device Breakdown
                    </h3>
                    <div className="space-y-3">
                      {marketingData.devices?.map((device, i) => {
                        const total = marketingData.devices.reduce((sum, d) => sum + d.visits, 0);
                        const percentage = total > 0 ? Math.round((device.visits / total) * 100) : 0;
                        return (
                          <div key={i} className="p-3 bg-surface-page rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-ink-soft capitalize flex items-center">
                                {device.device === 'mobile' ? <Smartphone className="w-4 h-4 mr-2" /> : 
                                 device.device === 'desktop' ? <Monitor className="w-4 h-4 mr-2" /> : null}
                                {device.device}
                              </span>
                              <span className="text-sm font-bold text-ink-soft">{percentage}%</span>
                            </div>
                            <div className="w-full bg-surface-muted rounded-full h-2">
                              <div 
                                className="bg-info h-2 rounded-full"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                      {(!marketingData.devices || marketingData.devices.length === 0) && (
                        <p className="text-ink-muted text-center py-4">No data yet</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Top Referrers & Campaigns */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
                    <h3 className="text-lg font-semibold text-ink-soft mb-4">Top Referrers</h3>
                    <div className="space-y-3">
                      {marketingData.top_referrers?.slice(0, 5).map((ref, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-surface-page rounded-lg">
                          <span className="text-sm font-medium text-ink-soft truncate max-w-[200px]">
                            {ref.referrer === 'Direct' ? '🔗 Direct' : ref.referrer}
                          </span>
                          <span className="text-sm font-bold text-ink-soft">{ref.visits}</span>
                        </div>
                      ))}
                      {(!marketingData.top_referrers || marketingData.top_referrers.length === 0) && (
                        <p className="text-ink-muted text-center py-4">No data yet</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
                    <h3 className="text-lg font-semibold text-ink-soft mb-4">UTM Campaigns</h3>
                    <div className="space-y-3">
                      {marketingData.campaigns?.slice(0, 5).map((campaign, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-surface-page rounded-lg">
                          <div>
                            <span className="text-sm font-medium text-ink-soft">{campaign.source}</span>
                            {campaign.campaign !== 'none' && (
                              <span className="text-small text-ink-muted ml-2">/ {campaign.campaign}</span>
                            )}
                          </div>
                          <span className="text-sm font-bold text-ink-soft">{campaign.visits}</span>
                        </div>
                      ))}
                      {(!marketingData.campaigns || marketingData.campaigns.length === 0) && (
                        <p className="text-ink-muted text-center py-4">No UTM campaigns tracked yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-surface-card rounded-card shadow-elevation-1 border p-8 text-center">
                <Target className="w-12 h-12 text-ink-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-ink-soft mb-2">No Marketing Data Yet</h3>
                <p className="text-ink-muted mb-4">
                  Marketing analytics will appear here once visitors start coming to your landing pages.
                </p>
                <p className="text-sm text-ink-muted">
                  Make sure you've:
                </p>
                <ul className="text-sm text-ink-muted mt-2 space-y-1">
                  <li>✓ Run the database migration (010_marketing_analytics.sql)</li>
                  <li>✓ Restarted the backend server</li>
                  <li>✓ Deployed the updated landing pages to Netlify</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ink-soft">User Management</h3>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-muted w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    className="pl-10 pr-4 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                </div>
                <button className="flex items-center space-x-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover">
                  <Plus className="w-4 h-4" />
                  <span>Add User</span>
                </button>
              </div>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-surface-card rounded-card shadow-elevation-1 border p-4">
                    <div className="animate-pulse flex items-center space-x-3">
                      <div className="w-10 h-10 bg-surface-muted rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-surface-muted rounded w-1/4"></div>
                        <div className="h-3 bg-surface-muted rounded w-1/3"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {users.map((userData) => (
                  <UserCard key={userData.id} user={userData} />
                ))}
                {users.length === 0 && (
                  <div className="bg-surface-card rounded-card shadow-elevation-1 border p-8 text-center">
                    <Users className="w-12 h-12 text-ink-muted mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-ink-soft mb-2">No users found</h3>
                    <p className="text-ink-muted">There are no users to display at the moment.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
              <h3 className="text-lg font-semibold text-ink-soft mb-4">System Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-surface-page rounded-lg">
                  <div>
                    <h4 className="font-medium text-ink-soft">Maintenance Mode</h4>
                    <p className="text-sm text-ink-muted">Temporarily disable user access for maintenance</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-surface-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-default after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-surface-page rounded-lg">
                  <div>
                    <h4 className="font-medium text-ink-soft">Email Notifications</h4>
                    <p className="text-sm text-ink-muted">Send admin notifications via email</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-surface-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-default after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6">
              <h3 className="text-lg font-semibold text-ink-soft mb-4">Danger Zone</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-danger-border rounded-lg bg-danger-tint">
                  <div>
                    <h4 className="font-medium text-danger">Clear Cache</h4>
                    <p className="text-sm text-danger">Clear all cached data and temporary files</p>
                  </div>
                  <button className="px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger/80">
                    Clear Cache
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ads' && <OpsAds />}
        {activeTab === 'expenses' && <OpsExpenses />}
        {activeTab === 'ordering' && <OpsOrdering />}
        {activeTab === 'inventory' && <OpsInventory />}
        {activeTab === 'finances' && <OpsFinances />}
      </div>
    </div>
  );
};
